import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Box } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { UserProfileEntityContainerWithAccordeon } from 'common/components/UserProfileEntityContainer/UserProfileEntityContainerWithAccordeon';
import { UserProfilesDataGrid } from 'common/components/UserProfilesDataGrid/UserProfilesDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmSortModel } from 'common/hooks/usePalmSortModel';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchLicenses } from 'common/services/api/Licenses/Licenses.thunk';
import { fetchUserProfileAclsAndLicenses } from 'common/services/api/UserProfileAcls/UserProfileAcls.thunk';
import {
    ActionDefinition,
    ColumnDefinition,
    ColumnType,
    DrawerEntitiesNames,
    Operations } from 'common/types/TypeDefs';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { getUserAssociatedLicenses } from 'common/utils/getAssociatedLicenseHelpers';
import { selectLicensesByTenantId } from 'features/licenses/licensesSlice';
import { selectUserProfileAclsWithLicenseByTenantId } from 'features/userProfileAcls/userProfileAclsSlice';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';
import { useSnackbar } from 'notistack';

const CONTAINER_INLINE_STYLES = {
    boxShadow: 'unset',
    marginBottom: '1rem',
    position: 'unset',
    backgroundColor: 'secondary.light',
    borderRadius: '4px',
    border: 0.5,
    borderColor: 'info.dark',
    flexBasis: '100%',
    marginTop: '24px',
    height: 'max-content'
};

type UserProfileLicenseListProps = {
    tenant: IUserProfileTenant;
};

export function UserProfileLicensesList({ tenant }: Readonly<UserProfileLicenseListProps>) {
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { userId } = useParams();

    const [licensesProcessing, setLicensesProcessing] = useState<boolean>(false);
    const [aclsWithLicensesProcessing, setAclsWithLicensesProcessing] = useState<boolean>(false);
    const [sortModel, setSortModel] = usePalmSortModel('id');

    const userProfileAclsWithLicenses = useAppSelector(selectUserProfileAclsWithLicenseByTenantId(tenant.id));
    const licenses = useAppSelector(selectLicensesByTenantId(tenant.id));
    const userProfileLicenses = userProfileAclsWithLicenses && licenses
        ? getUserAssociatedLicenses(licenses, userProfileAclsWithLicenses)
        : [];

    const openButtonDiasbled = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.MultiTenantRO ]);

    useEffect(() => {
        if(licenses)
            return;
        setLicensesProcessing(true);
        dispatch(fetchLicenses({ tenantId: tenant.id }))
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), 'User Profile Licenses', enqueueSnackbar);
            })
            .finally(() => {
                setLicensesProcessing(false);
            });
    }, []);

    useEffect(() => {
        if(userProfileAclsWithLicenses)
            return;
        setAclsWithLicensesProcessing(true);
        dispatch(fetchUserProfileAclsAndLicenses({ currentUserProfileTenant: tenant, userId: userId ?? '' }))
            .catch((error) => {
                handleListFetchError(extractStatusCode(error.message), 'User Profile Licenses', enqueueSnackbar);
            })
            .finally(() => {
                setAclsWithLicensesProcessing(false);
            });
    }, []);

    const openButtonLinkHandler = useCallback((licenseId: string) => {
        return `/${Routes.ApplicationSuites}/${tenant.applicationSuiteId}/${Routes.Tenants}/${tenant.id}/${Routes.Licenses}/${licenseId}/users`;
    }, []);

    const openButtonClickHandler = useCallback((licenseId: string) => {
        navigate(openButtonLinkHandler(licenseId));
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: openButtonDiasbled, urlToOpen: openButtonLinkHandler }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'id', columnName: 'License ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'companyKey', columnName: 'Company Key', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'billingRecord', columnName: 'Billing Record', columnType: ColumnType.Tooltip, copy: true },
        { columnId: 'startDate', columnName: 'Start Date', size: 0.7, columnType: ColumnType.DatetimeTooltip, copy: false },
        { columnId: 'endDate', columnName: 'End Date', size: 0.7, columnType: ColumnType.DatetimeTooltip, copy: false },
        { columnId: 'note', columnName: 'License Note', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', size: 0.4, columnType: ColumnType.Action, sortable: false }
    ];

    return (
        <UserProfileEntityContainerWithAccordeon
            title='Licenses'
            isCreateButtonDisabled={true}
            shouldShowCreateButton={false}
            containerInlineStyles={CONTAINER_INLINE_STYLES}
        >
            <Box sx={{ margin: '0 24px 24px 24px' }}>
                <UserProfilesDataGrid
                    tenant={tenant}
                    rows={userProfileLicenses}
                    actionDefinitions={actionDefinitions}
                    columnDefinitions={columnDefinitions}
                    processing={licensesProcessing || aclsWithLicensesProcessing}
                    title='Licenses'
                    currentEntityType={DrawerEntitiesNames.userProfileLicenses}
                    rowClick={openButtonDiasbled ? undefined : openButtonClickHandler}
                    rowLink={openButtonLinkHandler}
                    sortModel={sortModel}
                    clientSortHandler={setSortModel}
                />
            </Box>
        </UserProfileEntityContainerWithAccordeon>
    );
}
